'use client'

import * as React from 'react'
import dayjs from 'dayjs'
import { cn } from '../../../../../lib/utils'
import { type ChipValue } from '../../../Input/FilterChip'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '../../../Command'
import { Popover, PopoverContent, PopoverTrigger } from '../../../Popover'
import { Input } from '../../../Input'
import { Button } from '../../../Button'
import { Calendar } from '../../../Calendar'
import { TimeInput } from '../../../DateTimePicker/TimeInput'
import Icon from '../../../Icon'
import {
  AU_DATE_FORMAT,
  AU_DATETIME_FORMAT,
  AU_TIME_FORMAT,
} from '../../../DatePicker/utils'
import {
  formatRelativeDateFilterLabel,
  formatRelativeDateFilterValue,
  getRelativeDateFilterPresetsForOp,
  isRelativeDateFilterValue,
  type RelativeDateFilterPreset,
} from '../../relativeDateFilter'

type Condition = {
  dropdownLabel: string
  label: string
  value: string
}

// Helper functions to generate date filter options
const getDayOfWeekOptions = () => [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
]

const getDayOfMonthOptions = () =>
  Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  }))

const getMonthOfYearOptions = () => [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]

const getHourOfDayOptions = () =>
  Array.from({ length: 24 }, (_, i) => ({
    value: String(i),
    label: `${i}:00`,
  }))

const getDateFilterOptions = (conditionValue: string) => {
  switch (conditionValue) {
    case 'day_of_week':
      return getDayOfWeekOptions()
    case 'day_of_month':
      return getDayOfMonthOptions()
    case 'month_of_year':
      return getMonthOfYearOptions()
    case 'hour_of_day':
      return getHourOfDayOptions()
    default:
      return []
  }
}

const OPTION_FIELD_CONDITIONS: Condition[] = [
  { dropdownLabel: 'is...', label: 'is', value: 'eq' },
  { dropdownLabel: 'is not...', label: 'is not', value: 'neq' },
]

const getConditionsForType = (dataType: string): Condition[] => {
  switch (dataType) {
    case 'DATE':
    case 'DATE_TIME':
    case 'TIME':
      return [
        { dropdownLabel: 'is before...', label: 'is before', value: 'lt' },
        { dropdownLabel: 'is after...', label: 'is after', value: 'gt' },
        {
          dropdownLabel: 'day of week...',
          label: 'day of week',
          value: 'day_of_week',
        },
        {
          dropdownLabel: 'month of year...',
          label: 'month of year',
          value: 'month_of_year',
        },
        {
          dropdownLabel: 'hour of day...',
          label: 'hour of day',
          value: 'hour_of_day',
        },
      ]
    case 'NUMBER':
      return [
        {
          dropdownLabel: 'is greater than...',
          label: 'is greater than',
          value: 'gt',
        },
        {
          dropdownLabel: 'is less than...',
          label: 'is less than',
          value: 'lt',
        },
        { dropdownLabel: 'is equal to...', label: 'is equal to', value: 'eq' },
        {
          dropdownLabel: 'is not equal to...',
          label: 'is not equal to',
          value: 'neq',
        },
      ]
    case 'BOOLEAN':
      return [{ dropdownLabel: 'is...', label: 'is', value: 'eq' }]
    case 'RELATION':
      return [
        { dropdownLabel: 'is...', label: 'is', value: 'eq' },
        { dropdownLabel: 'is not...', label: 'is not', value: 'neq' },
      ]
    case 'STRING':
    default:
      return [
        { dropdownLabel: 'contains...', label: 'contains', value: 'contains' },
        { dropdownLabel: 'is...', label: 'is', value: 'eq' },
        { dropdownLabel: 'is not...', label: 'is not', value: 'neq' },
        {
          dropdownLabel: 'starts with...',
          label: 'starts with',
          value: 'starts_with',
        },
        {
          dropdownLabel: 'ends with...',
          label: 'ends with',
          value: 'ends_with',
        },
        {
          dropdownLabel: 'does not contain...',
          label: 'does not contain',
          value: 'not_contains',
        },
      ]
  }
}

export type QueryConditionProps = {
  field: {
    label: string
    value: string
    modelLabel?: string
    showModelLabel?: boolean
    dataType:
      | 'STRING'
      | 'NUMBER'
      | 'DATE'
      | 'DATE_TIME'
      | 'TIME'
      | 'BOOLEAN'
      | 'RELATION'
    options?: {
      multipleChoice: boolean
      items?: Array<{
        id?: string
        value: string
        label: string
      }>
    }
    searchOptions?: (
      query: string,
    ) => Promise<Array<{ value: string; label: string }>>
  }
  selectedCondition?: {
    label: string
    value: string
  }
  comparison: {
    label: string | null
    value: string | null
    options: ChipValue[]
  }
  onConditionChange?: (value: ChipValue) => void
  onComparisonChange?: (value: ChipValue) => void
  onDelete?: () => void
  isDeleteMode?: boolean
  onDeleteModeChange: (isDeleteMode: boolean) => void
  autoOpen?: boolean
  onOpenStateChange?: (isOpen: boolean) => void
}

export const QueryCondition: React.FC<QueryConditionProps> = ({
  field,
  selectedCondition,
  comparison,
  onConditionChange,
  onComparisonChange,
  onDelete,
  isDeleteMode: externalIsDeleteMode,
  onDeleteModeChange,
  autoOpen,
  onOpenStateChange,
}) => {
  const [conditionOpen, setConditionOpen] = React.useState(false)
  const [comparisonOpen, setComparisonOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState(comparison.value || '')
  const [localComparisonLabel, setLocalComparisonLabel] = React.useState<
    string | null
  >(comparison.label ?? null)
  const [localComparisonValue, setLocalComparisonValue] = React.useState<
    string | null
  >(comparison.value ?? null)
  const [dateValue, setDateValue] = React.useState<Date | undefined>(() => {
    if (!comparison.value || isRelativeDateFilterValue(comparison.value)) {
      return undefined
    }
    const parsed = new Date(comparison.value)
    return Number.isNaN(parsed.getTime()) ? undefined : parsed
  })
  const [showCustomDatePicker, setShowCustomDatePicker] = React.useState(false)
  const [customDraftDate, setCustomDraftDate] = React.useState<Date | undefined>(
    undefined,
  )
  const didAutoOpenRef = React.useRef<boolean>(false)
  const conditionOpenedAtRef = React.useRef<number>(0)
  const comparisonOpenedAtRef = React.useRef<number>(0)

  const isDeleteMode = externalIsDeleteMode ?? false
  const hasOptions = !!field.options
  const conditions = React.useMemo(
    () =>
      hasOptions
        ? OPTION_FIELD_CONDITIONS
        : getConditionsForType(field.dataType),
    [field.dataType, hasOptions],
  )
  const currentCondition = selectedCondition ?? conditions[0]
  // Option/enum fields show is / is not; other types keep their type-specific operators.
  const showCondition = true
  const showDateFilterSelect =
    !hasOptions &&
    (field.dataType === 'DATE' ||
      field.dataType === 'DATE_TIME' ||
      field.dataType === 'TIME') &&
    (currentCondition?.value === 'day_of_week' ||
      currentCondition?.value === 'day_of_month' ||
      currentCondition?.value === 'month_of_year' ||
      currentCondition?.value === 'hour_of_day')

  const showInput =
    !hasOptions &&
    (field.dataType === 'STRING' || field.dataType === 'NUMBER') &&
    !showDateFilterSelect
  const showBooleanSelect = !hasOptions && field.dataType === 'BOOLEAN'
  const showDatePicker =
    !hasOptions &&
    (field.dataType === 'DATE' ||
      field.dataType === 'DATE_TIME' ||
      field.dataType === 'TIME') &&
    !showDateFilterSelect

  const isSelectField = !!field.options?.items && field.options.items.length > 0
  const isMultiSelect = !!field.options?.multipleChoice
  const supportsRemoteSearch = typeof field.searchOptions === 'function'

  const [remoteItems, setRemoteItems] = React.useState<Array<{
    value: string
    label: string
  }> | null>(null)
  const [remoteLoading, setRemoteLoading] = React.useState(false)
  const [remoteQuery, setRemoteQuery] = React.useState('')

  // Fetch remote options when searchOptions is provided and query changes
  React.useEffect(() => {
    let cancelled = false
    const fetchOptions = async () => {
      if (!supportsRemoteSearch) return
      setRemoteLoading(true)
      try {
        const q = remoteQuery || ''
        const results = await field.searchOptions!(q)
        if (!cancelled) {
          setRemoteItems(Array.isArray(results) ? results : [])
        }
      } catch (_e) {
        if (!cancelled) setRemoteItems([])
      } finally {
        if (!cancelled) setRemoteLoading(false)
      }
    }
    fetchOptions()
    return () => {
      cancelled = true
    }
  }, [field, remoteQuery, supportsRemoteSearch])

  // Initialize input value when condition changes
  React.useEffect(() => {
    if (showDateFilterSelect && currentCondition?.value) {
      const options = getDateFilterOptions(currentCondition.value)
      if (options.length > 0 && !inputValue) {
        // Set default to first option if no value is selected
        const firstOption = options[0]
        if (firstOption) {
          setInputValue(firstOption.value)
          onComparisonChange?.({
            label: firstOption.label,
            value: firstOption.value,
          })
        }
      }
    }
  }, [
    currentCondition?.value,
    showDateFilterSelect,
    inputValue,
    onComparisonChange,
  ])

  // Keep local comparison label/value in sync with props when parent updates
  React.useEffect(() => {
    setLocalComparisonLabel(comparison.label ?? null)
    setLocalComparisonValue(comparison.value ?? null)
    if (!comparison.value || isRelativeDateFilterValue(comparison.value)) {
      setDateValue(undefined)
      return
    }
    const parsed = new Date(comparison.value)
    setDateValue(Number.isNaN(parsed.getTime()) ? undefined : parsed)
  }, [comparison.label, comparison.value])

  // Focus helper for comparison input when available
  const focusComparisonInput = React.useCallback(() => {
    const el = document.getElementById(
      `${field.value}-comparison-input`,
    ) as HTMLInputElement | null
    if (el) {
      try {
        el.focus()
        // Select existing text so users can type over it immediately
        if (typeof el.select === 'function') {
          el.select()
        }
      } catch {
        // ignore
      }
    }
  }, [field.value])

  const handleDeleteClick = () => {
    onDeleteModeChange(true)
  }

  const handleTriggerClick = () => {
    if (isDeleteMode) {
      onDeleteModeChange(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    onComparisonChange?.({
      label: value,
      value: value,
    })
  }

  const getDatePickerFormat = () => {
    switch (field.dataType) {
      case 'TIME':
        return AU_TIME_FORMAT
      case 'DATE':
        return AU_DATE_FORMAT
      case 'DATE_TIME':
        return AU_DATETIME_FORMAT
      default:
        return AU_DATETIME_FORMAT
    }
  }

  const handleDateChange = (date: Date | undefined) => {
    setDateValue(date)
    if (date) {
      const formattedDate = date.toISOString()
      onComparisonChange?.({
        label: dayjs(date).format(getDatePickerFormat()),
        value: formattedDate,
      })
    } else {
      onComparisonChange?.({
        label: '',
        value: '',
      })
    }
    setShowCustomDatePicker(false)
    setCustomDraftDate(undefined)
    setComparisonOpen(false)
    onOpenStateChange?.(false)
  }

  const handleRelativeDateSelect = (preset: RelativeDateFilterPreset) => {
    const op = currentCondition?.value ?? 'lt'
    const token = formatRelativeDateFilterValue(preset)
    const label = formatRelativeDateFilterLabel(preset, op) ?? token
    setDateValue(undefined)
    setLocalComparisonLabel(label)
    setLocalComparisonValue(token)
    onComparisonChange?.({ label, value: token })
    setShowCustomDatePicker(false)
    setCustomDraftDate(undefined)
    setComparisonOpen(false)
    onOpenStateChange?.(false)
  }

  const relativeDatePresets = React.useMemo(
    () => getRelativeDateFilterPresetsForOp(currentCondition?.value ?? 'lt'),
    [currentCondition?.value],
  )

  const dateDisplayLabel = React.useMemo(() => {
    const value = localComparisonValue ?? comparison.value
    const label = localComparisonLabel ?? comparison.label
    if (value && isRelativeDateFilterValue(value)) {
      return (
        formatRelativeDateFilterLabel(value, currentCondition?.value ?? 'lt') ||
        label ||
        'Select...'
      )
    }
    if (dateValue) {
      return dayjs(dateValue).format(getDatePickerFormat())
    }
    if (label) {
      const asDate = new Date(label)
      if (!Number.isNaN(asDate.getTime()) && label.includes('T')) {
        return dayjs(asDate).format(getDatePickerFormat())
      }
      return label
    }
    return 'Select...'
  }, [
    localComparisonValue,
    localComparisonLabel,
    comparison.value,
    comparison.label,
    currentCondition?.value,
    dateValue,
    field.dataType,
  ])

  React.useEffect(() => {
    // Only auto-open once per chip mount to avoid reopen after Enter closes it.
    // Also ensure neither popover is already open before opening.
    if (
      autoOpen &&
      !didAutoOpenRef.current &&
      !conditionOpen &&
      !comparisonOpen
    ) {
      didAutoOpenRef.current = true
      setComparisonOpen(true)
      onOpenStateChange?.(true)
    }
  }, [autoOpen, conditionOpen, comparisonOpen, onOpenStateChange])

  // When comparison popover is opened, focus appropriate inner input/search
  React.useEffect(() => {
    if (!comparisonOpen) return
    const targetId = showInput
      ? `${field.value}-comparison-input`
      : showDateFilterSelect
        ? `${field.value}-datefilter-search`
        : isSelectField || supportsRemoteSearch || isMultiSelect
          ? `${field.value}-select-search`
          : `${field.value}-options-search`
    const focusLater = () => {
      const el = document.getElementById(targetId) as HTMLInputElement | null
      if (el) {
        try {
          el.focus()
          el.select?.()
        } catch {
          // ignore
        }
      }
    }
    // slight delay to allow popover content to mount
    const t = setTimeout(focusLater, 10)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonOpen])

  return (
    <div
      className={cn(
        'inline-flex items-stretch p-0.5 bg-background/50 rounded-md text-xs group transition-colors whitespace-nowrap relative divide-x divide-border/30',
        'bg-accent',
        isDeleteMode && 'bg-foreground/20',
      )}
      data-id={field.value}
    >
      {/* Delete button */}
      {isDeleteMode && (
        <button
          onClick={onDelete}
          className="absolute -top-1.5 -right-1.5 p-1.5 rounded-full bg-foreground/50 hover:bg-foreground/80 transition-colors"
        ></button>
      )}

      {/* Field with delete trigger */}
      <div
        className={cn(
          'hover:bg-accent/90 transition-colors rounded-l-md',
          isDeleteMode && 'bg-accent/20',
        )}
      >
        <button
          onClick={handleDeleteClick}
          className="px-1.5 py-0.5 flex items-center gap-1 text-xs min-w-fit h-full w-full "
        >
          <span className="truncate flex items-center gap-1">
            {field.showModelLabel &&
              typeof field.value === 'string' &&
              !field.value.includes('.') &&
              field.modelLabel &&
              field.modelLabel !== field.label && (
                <>
                  <span className="text-2xs text-muted-foreground/70">
                    {field.modelLabel}
                  </span>
                  <span className="text-2xs text-muted-foreground/50">•</span>
                </>
              )}
            <span>{field.label}</span>
          </span>
        </button>
      </div>

      {/* Condition Dropdown */}
      {showCondition && (
        <div className="hover:bg-accent/90 transition-colors">
          <Popover
            open={conditionOpen}
            onOpenChange={(open) => {
              if (open) {
                // Ensure only one popover is open at a time
                setComparisonOpen(false)
                conditionOpenedAtRef.current = Date.now()
              }
              // Ignore immediate close right after opening caused by focus transitions
              if (!open && Date.now() - conditionOpenedAtRef.current < 300) {
                setConditionOpen(true)
                onOpenStateChange?.(true)
                return
              }
              setConditionOpen(open)
              onOpenStateChange?.(open || comparisonOpen)
            }}
          >
            <PopoverTrigger asChild>
              <button
                onMouseDown={(e) => {
                  // Avoid outer handlers from racing; keep event local
                  e.stopPropagation()
                }}
                onClick={(e) => {
                  // If the value editor is open, close it and open condition instead
                  if (comparisonOpen) {
                    e.preventDefault()
                    e.stopPropagation()
                    setComparisonOpen(false)
                    // open condition on next tick to avoid flicker
                    setTimeout(() => {
                      setConditionOpen(true)
                      onOpenStateChange?.(true)
                    }, 0)
                    return
                  }
                  handleTriggerClick()
                }}
                className="px-1.5 py-0.5 h-full text-xs min-w-fit whitespace-nowrap hover:bg-foreground/10"
              >
                {currentCondition ? currentCondition.label : ''}
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[200px] p-0 -mt-[29px]"
              align="start"
              onInteractOutside={(e) => {
                if (Date.now() - conditionOpenedAtRef.current < 300) {
                  e.preventDefault()
                }
              }}
            >
              <Command>
                <CommandInput
                  id={`${field.value}-options-search`}
                  placeholder="Search..."
                />
                <CommandList>
                  <CommandEmpty>No results found.</CommandEmpty>
                  <CommandGroup>
                    {conditions.map((option) => (
                      <CommandItem
                        key={option.value}
                        onSelect={() => {
                          onConditionChange?.(option)
                          setConditionOpen(false)
                          setShowCustomDatePicker(false)
                          setComparisonOpen(true)
                          onOpenStateChange?.(true)
                        }}
                      >
                        {option.dropdownLabel}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Comparison Value Section */}
      <div className="hover:bg-accent/90 transition-colors rounded-r-md">
        {showInput ? (
          <Popover
            open={comparisonOpen}
            onOpenChange={(open) => {
              if (open) {
                comparisonOpenedAtRef.current = Date.now()
              }
              // Ignore immediate close right after opening
              if (!open && Date.now() - comparisonOpenedAtRef.current < 300) {
                setComparisonOpen(true)
                onOpenStateChange?.(true)
                return
              }
              setComparisonOpen(open)
              onOpenStateChange?.(open || conditionOpen)
            }}
          >
            <PopoverTrigger asChild>
              <button
                onMouseDown={(e) => {
                  e.stopPropagation()
                }}
                onClick={handleTriggerClick}
                className="px-1.5 py-0.5 h-full text-xs min-w-fit whitespace-nowrap hover:bg-foreground/10 rounded-r-md"
              >
                {inputValue || ''}
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[200px] p-0 -mt-[29px]"
              align="start"
              onInteractOutside={(e) => {
                if (Date.now() - comparisonOpenedAtRef.current < 300) {
                  e.preventDefault()
                }
              }}
            >
              <Input
                id={`${field.value}-comparison-input`}
                type={
                  field.dataType === 'NUMBER' ||
                  currentCondition?.value === 'day_of_week' ||
                  currentCondition?.value === 'day_of_month' ||
                  currentCondition?.value === 'month_of_year' ||
                  currentCondition?.value === 'hour_of_day'
                    ? 'number'
                    : 'text'
                }
                value={inputValue}
                handleChange={handleInputChange}
                handleKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setComparisonOpen(false)
                    onOpenStateChange?.(false)
                  }
                }}
                debounceTime={400}
                placeholder={(() => {
                  if (currentCondition?.value === 'day_of_week')
                    return '1-7 (Mon=1)...'
                  if (currentCondition?.value === 'day_of_month')
                    return '1-31...'
                  if (currentCondition?.value === 'month_of_year')
                    return '1-12...'
                  if (currentCondition?.value === 'hour_of_day')
                    return '0-23...'
                  return 'Enter value...'
                })()}
                autoFocus
              />
            </PopoverContent>
          </Popover>
        ) : showDatePicker ? (
          <div className="hover:bg-foreground/10 transition-colors rounded-r-md">
            <Popover
              open={comparisonOpen}
              onOpenChange={(open) => {
                if (open) {
                  comparisonOpenedAtRef.current = Date.now()
                  setShowCustomDatePicker(false)
                  setCustomDraftDate(dateValue)
                }
                if (!open && Date.now() - comparisonOpenedAtRef.current < 300) {
                  setComparisonOpen(true)
                  onOpenStateChange?.(true)
                  return
                }
                setComparisonOpen(open)
                if (!open) {
                  setShowCustomDatePicker(false)
                  setCustomDraftDate(undefined)
                }
                onOpenStateChange?.(open || conditionOpen)
              }}
            >
              <PopoverTrigger asChild>
                <button
                  onMouseDown={(e) => {
                    e.stopPropagation()
                  }}
                  onClick={handleTriggerClick}
                  className="px-1.5 py-0.5 h-full text-xs min-w-fit whitespace-nowrap hover:bg-foreground/10 rounded-r-md"
                >
                  {dateDisplayLabel}
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0"
                align="start"
                onInteractOutside={(e) => {
                  if (Date.now() - comparisonOpenedAtRef.current < 300) {
                    e.preventDefault()
                  }
                }}
              >
                {showCustomDatePicker ? (
                  <div className="w-72">
                    <div className="p-3 border-b">
                      <button
                        type="button"
                        onClick={() => setShowCustomDatePicker(false)}
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      >
                        <Icon name="ChevronLeft" size={14} />
                        <span>Back</span>
                      </button>
                    </div>
                    {field.dataType !== 'TIME' && (
                      <Calendar
                        mode="single"
                        selected={customDraftDate}
                        onSelect={(date) => {
                          if (!date) return
                          if (
                            field.dataType === 'DATE_TIME' &&
                            customDraftDate
                          ) {
                            const prev = dayjs(customDraftDate)
                            setCustomDraftDate(
                              dayjs(date)
                                .hour(prev.hour())
                                .minute(prev.minute())
                                .second(prev.second())
                                .toDate(),
                            )
                          } else if (field.dataType === 'DATE') {
                            setCustomDraftDate(dayjs(date).startOf('day').toDate())
                          } else {
                            setCustomDraftDate(date)
                          }
                        }}
                        initialFocus
                        className="w-full p-0"
                      />
                    )}
                    {field.dataType !== 'DATE' && (
                      <div className="flex justify-center py-2 border-t">
                        <TimeInput
                          value={customDraftDate || new Date()}
                          onChange={(date) => {
                            if (!date) return
                            setCustomDraftDate(date)
                          }}
                          use12Hour
                        />
                      </div>
                    )}
                    <div className="p-3 border-t">
                      <Button
                        variant="default"
                        size="sm"
                        className="w-full"
                        label="Apply"
                        isDisabled={!customDraftDate}
                        handleClick={() => handleDateChange(customDraftDate)}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="w-64 p-1">
                    {relativeDatePresets.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => handleRelativeDateSelect(preset)}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm',
                          'hover:bg-accent transition-colors text-left',
                        )}
                      >
                        <Icon
                          name="Clock"
                          size={16}
                          className="text-muted-foreground"
                        />
                        <span>{preset.label}</span>
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setCustomDraftDate(dateValue ?? new Date())
                        setShowCustomDatePicker(true)
                      }}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm',
                        'hover:bg-accent transition-colors text-left',
                      )}
                    >
                      <Icon
                        name="Calendar"
                        size={16}
                        className="text-muted-foreground"
                      />
                      <span>Custom date...</span>
                    </button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
        ) : showDateFilterSelect ? (
          <Popover
            open={comparisonOpen}
            onOpenChange={(open) => {
              setComparisonOpen(open)
              onOpenStateChange?.(open || conditionOpen)
            }}
          >
            <PopoverTrigger asChild>
              <button
                onClick={handleTriggerClick}
                className="px-1.5 py-0.5 h-full text-xs min-w-fit whitespace-nowrap hover:bg-foreground/10 rounded-r-md"
              >
                {(() => {
                  const options = getDateFilterOptions(
                    currentCondition?.value || '',
                  )
                  const selectedOption = options.find(
                    (opt) => opt.value === inputValue,
                  )
                  return selectedOption?.label || 'Select...'
                })()}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0 -mt-[29px]" align="start">
              <Command>
                <CommandInput
                  id={`${field.value}-datefilter-search`}
                  placeholder="Search..."
                />
                <CommandList>
                  <CommandEmpty>No results found.</CommandEmpty>
                  <CommandGroup>
                    {getDateFilterOptions(currentCondition?.value || '').map(
                      (option) => (
                        <CommandItem
                          key={option.value}
                          onSelect={() => {
                            setInputValue(option.value)
                            onComparisonChange?.({
                              label: option.label,
                              value: option.value,
                            })
                            setComparisonOpen(false)
                            onOpenStateChange?.(false)
                          }}
                        >
                          {option.label}
                        </CommandItem>
                      ),
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ) : showBooleanSelect ? (
          <Popover
            open={comparisonOpen}
            onOpenChange={(open) => {
              setComparisonOpen(open)
              onOpenStateChange?.(open || conditionOpen)
            }}
          >
            <PopoverTrigger asChild>
              <button
                onClick={handleTriggerClick}
                className="px-1.5 py-0.5 h-full text-xs min-w-fit whitespace-nowrap hover:bg-foreground/10"
              >
                {comparison.value === 'true'
                  ? 'Yes'
                  : comparison.value === 'false'
                    ? 'No'
                    : 'Select...'}
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[200px] p-0 -mt-[29px]"
              align="start"
              onInteractOutside={(e) => {
                if (Date.now() - comparisonOpenedAtRef.current < 300) {
                  e.preventDefault()
                }
              }}
            >
              <Command>
                <CommandList>
                  <CommandGroup>
                    <CommandItem
                      key="__none__"
                      onSelect={() => {
                        onComparisonChange?.({
                          label: '( None )',
                          value: '__NONE__',
                        })
                        setComparisonOpen(false)
                        onOpenStateChange?.(false)
                      }}
                    >
                      ( None )
                    </CommandItem>
                    {[
                      { label: 'Yes', value: 'true' },
                      { label: 'No', value: 'false' },
                    ].map((option) => (
                      <CommandItem
                        key={option.value}
                        onSelect={() => {
                          onComparisonChange?.(option)
                          setComparisonOpen(false)
                          onOpenStateChange?.(false)
                        }}
                      >
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ) : isSelectField || supportsRemoteSearch ? (
          <Popover
            open={comparisonOpen}
            onOpenChange={(open) => {
              setComparisonOpen(open)
              onOpenStateChange?.(open || conditionOpen)
            }}
          >
            <PopoverTrigger asChild>
              <button
                onClick={handleTriggerClick}
                className="px-1.5 py-0.5 h-full text-xs min-w-fit whitespace-nowrap hover:bg-foreground/10"
              >
                {localComparisonLabel || 'Select...'}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0 -mt-[29px]" align="start">
              <Command>
                <CommandInput
                  placeholder="Search..."
                  value={remoteQuery}
                  onValueChange={(v) => setRemoteQuery(v)}
                />
                <CommandList>
                  <CommandEmpty>No results found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      key="__none__"
                      onSelect={() => {
                        setLocalComparisonLabel('( None )')
                        setLocalComparisonValue('__NONE__')
                        onComparisonChange?.({
                          label: '( None )',
                          value: '__NONE__',
                        })
                        setComparisonOpen(false)
                        onOpenStateChange?.(false)
                      }}
                    >
                      ( None )
                    </CommandItem>
                    {(supportsRemoteSearch
                      ? remoteItems || []
                      : field.options?.items || []
                    ).map((option: { value: string; label: string }) => (
                      <CommandItem
                        key={option.value}
                        onSelect={() => {
                          setLocalComparisonLabel(option.label)
                          setLocalComparisonValue(option.value)
                          onComparisonChange?.({
                            label: option.label,
                            value: option.value,
                          })
                          setComparisonOpen(false)
                          onOpenStateChange?.(false)
                        }}
                      >
                        {option.label}
                      </CommandItem>
                    ))}
                    {supportsRemoteSearch && remoteLoading && (
                      <CommandItem key="__loading__" data-disabled>
                        Loading...
                      </CommandItem>
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ) : isMultiSelect ? (
          <Popover
            open={comparisonOpen}
            onOpenChange={(open) => {
              setComparisonOpen(open)
              onOpenStateChange?.(open || conditionOpen)
            }}
          >
            <PopoverTrigger asChild>
              <button
                onClick={handleTriggerClick}
                className="px-1.5 py-0.5 h-full text-xs min-w-fit whitespace-nowrap hover:bg-foreground/10"
              >
                {localComparisonLabel || 'Select...'}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[240px] p-0 -mt-[29px]" align="start">
              <Command>
                <CommandInput
                  id={`${field.value}-select-search`}
                  placeholder="Search..."
                />
                <CommandList>
                  <CommandEmpty>No results found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      key="__none__"
                      onSelect={() => {
                        setLocalComparisonLabel('( None )')
                        setLocalComparisonValue('__NONE__')
                        onComparisonChange?.({
                          label: '( None )',
                          value: '__NONE__',
                        })
                      }}
                    >
                      ( None )
                    </CommandItem>
                    {field.options?.items?.map(
                      (option: { value: string; label: string }) => (
                        <CommandItem
                          key={option.value}
                          onSelect={() => {
                            // For multiselect, store a CSV list in value, label shows joined labels
                            const current = comparison.value
                              ? comparison.value.split(',')
                              : []
                            const has = current.includes(option.value)
                            const next = has
                              ? current.filter((v) => v !== option.value)
                              : [...current, option.value]
                            const nextLabel = (field.options?.items || [])
                              .filter((o: { value: string; label: string }) =>
                                next.includes(o.value),
                              )
                              .map(
                                (o: { value: string; label: string }) =>
                                  o.label,
                              )
                              .join(', ')
                            setLocalComparisonLabel(nextLabel || '')
                            setLocalComparisonValue(next.join(','))
                            onComparisonChange?.({
                              label: nextLabel || '',
                              value: next.join(','),
                            })
                          }}
                        >
                          {option.label}
                        </CommandItem>
                      ),
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ) : (
          <Popover
            open={comparisonOpen}
            onOpenChange={(open) => {
              setComparisonOpen(open)
              onOpenStateChange?.(open || conditionOpen)
            }}
          >
            <PopoverTrigger asChild>
              <button
                onClick={handleTriggerClick}
                className="px-1.5 py-0.5 h-full text-xs min-w-fit whitespace-nowrap hover:bg-foreground/10"
              >
                {localComparisonLabel || ''}
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[200px] p-0 -mt-[29px]"
              align="start"
              onInteractOutside={(e) => {
                if (Date.now() - comparisonOpenedAtRef.current < 300) {
                  e.preventDefault()
                }
              }}
            >
              <Command>
                <CommandInput
                  id={`${field.value}-select-search`}
                  placeholder="Search..."
                />
                <CommandList>
                  <CommandEmpty>No results found.</CommandEmpty>
                  <CommandGroup>
                    {comparison.options.map((option) => (
                      <CommandItem
                        key={option.value}
                        onSelect={() => {
                          setLocalComparisonLabel(option.label ?? '')
                          setLocalComparisonValue(option.value ?? '')
                          onComparisonChange?.(option)
                          setComparisonOpen(false)
                          onOpenStateChange?.(false)
                        }}
                      >
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  )
}
